import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function QuickView({ product, onClose }) {
  const { addItem, setIsOpen } = useCart();
  const [size, setSize] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    setSize(null);
    setImgIndex(0);
  }, [product]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const add = () => {
    if (!size) {
      toast.error("Sélectionnez une taille.");
      return;
    }
    addItem(product, size);
    onClose();
    setIsOpen(true);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
      />
      <div className="pointer-events-none fixed inset-0 z-50 flex overflow-y-auto p-4">
        <motion.div
          data-testid="quickview-modal"
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.98 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-auto relative m-auto grid w-full max-w-4xl grid-cols-1 border border-white/10 bg-coal md:h-[85vh] md:grid-cols-2"
        >
          <button
            data-testid="quickview-close-button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center border border-white/10 bg-ink/60 text-zinc-300 backdrop-blur-md transition-colors duration-300 hover:border-ember hover:text-ember"
            aria-label="Close quick view"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="relative aspect-[4/5] bg-ink md:aspect-auto md:h-full">
            <img
              src={`${product.gallery[imgIndex]}&w=1000&auto=format`}
              alt={product.name}
              className="h-full w-full object-cover"
            />
            {product.gallery.length > 1 && (
              <div className="absolute bottom-4 left-4 flex gap-2">
                {product.gallery.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`quickview-thumb-${i}`}
                    onClick={() => setImgIndex(i)}
                    className={`h-12 w-10 overflow-hidden border transition-colors duration-300 ${
                      i === imgIndex ? "border-ember" : "border-white/20 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={`${g}&w=200&auto=format`} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col p-7 sm:p-10 md:h-full md:overflow-y-auto">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">{product.category}</p>
            <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight sm:text-3xl">
              {product.name}
            </h3>
            <p className="mt-3 font-mono text-lg text-zinc-200">€{product.price.toFixed(0)}</p>
            <p className="mt-5 text-sm leading-relaxed text-zinc-400">{product.description}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              {product.fabric}
            </p>

            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Taille</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-button-${s}`}
                    onClick={() => setSize(s)}
                    className={`flex h-11 min-w-11 items-center justify-center border px-3 font-mono text-xs transition-colors duration-300 ${
                      size === s
                        ? "border-ember bg-ember text-white"
                        : "border-white/15 text-zinc-400 hover:border-zinc-400 hover:text-zinc-100"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <button
              data-testid="add-to-cart-button"
              onClick={add}
              className="mt-8 flex h-14 w-full shrink-0 items-center justify-center gap-3 bg-ember font-mono text-[11px] uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-emberhover"
            >
              <ShoppingBag className="h-4 w-4" /> Ajouter au panier
            </button>
            <p className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">
              Édition limitée — Paris atelier
            </p>
          </div>
        </motion.div>
      </div>
    </>
  );
}
